import { HttpClient } from "../../client/HttpClient";
import {
  ChallengeAction,
  LivenessCreateRequest,
  LivenessCreateResponse,
  LivenessSubmitResponse,
} from "../../types/liveness";
import { collectDeviceMetadata } from "../../utils/device";
import { CameraManager } from "../../camera/CameraManager";
import { VideoRecorder } from "../../camera/VideoRecorder";
import { FaceDetectorEngine } from "../../camera/FaceDetector";
import { ActionDetector } from "../../camera/ActionDetector";
import { LivenessUI } from "./LivenessUI";

/**
 * Liveness module — 3-step flow aligned with Adhere backend.
 *
 * Flow:
 *   Step 1: liveness.create()  → POST /v1/sdk/liveness/create/  (multipart)
 *   Step 2: [camera runs, user performs challenge actions]
 *   Step 3: liveness.submit()  → POST /v1/sdk/liveness/submit/  (multipart)
 *
 * After submit, the backend returns status: "processing".
 * Final results are delivered via webhook (liveness.completed event).
 */
export class LivenessModule {
  constructor(private http: HttpClient) { }

  /**
   * Step 1: Create a liveness challenge entry.
   *
   * POST /v1/sdk/liveness/create/
   * Auth: x-access-token: <sessionToken>
   * Content-Type: multipart/form-data
   */
  async create(params: LivenessCreateRequest): Promise<LivenessCreateResponse> {
    const formData = new FormData();
    formData.append("identifier", params.identifier);
    formData.append("identifier_type", params.identifier_type);
    formData.append("country", params.country);
    formData.append(
      "challenge_actions",
      JSON.stringify(params.challenge_actions)
    );
    if (params.autoshot_file) {
      formData.append("autoshot", params.autoshot_file, "autoshot.jpg");
    }
    
    // Handle document upload - support both legacy and new front/back fields
    if (params.document) {
      formData.append("document", params.document, "document.jpg");
      if (params.document_back) {
        formData.append("document_back", params.document_back, "document_back.jpg");
      }
    } else if (params.document_front) {
      // Legacy support - map to document for backward compatibility
      formData.append("document", params.document_front, "document_front.jpg");
      if (params.document_back) {
        formData.append("document_back", params.document_back, "document_back.jpg");
      }
    } else if (params.id_file) {
      // Legacy support - map to document for backward compatibility
      formData.append("document", params.id_file, "id_document.jpg");
    }
    
    if (params.identity_check) {
      formData.append("identity_check", String(params.identity_check));
    }
    if (params.snapshot_file) {
      formData.append("snapshot", params.snapshot_file, "snapshot.jpg");
    }
    if (params.device_type) {
      formData.append("device_type", params.device_type);
    }
    if (params.os_name) {
      formData.append("os_name", params.os_name);
    }
    if (params.browser_timezone) {
      formData.append("browser_timezone", params.browser_timezone);
    }
    if (params.fingerprint_id) {
      formData.append("fingerprint_id", params.fingerprint_id);
    }

    return this.http.uploadWithSession<LivenessCreateResponse>(
      "/v1/sdk/liveness/create/",
      formData
    );
  }

  /**
   * Step 3: Submit liveness video recording.
   *
   * POST /v1/sdk/liveness/submit/
   * Auth: x-access-token: <sessionToken>
   * Content-Type: multipart/form-data
   *
   * Note: After submission, the session is revoked (single-use).
   * The backend returns status: "processing" — results come via webhook.
   */
  async submit(
    entryId: number,
    videoBlob: Blob,
    snapshotBlob: Blob,
    autoshotBlob?: Blob,
    videoDurationSeconds?: number,
    videoSizeBytes?: number
  ): Promise<LivenessSubmitResponse> {
    const formData = new FormData();
    formData.append("entry", String(entryId));
    formData.append("video", videoBlob, "liveness.webm");
    formData.append("snapshot", snapshotBlob, "snapshot.jpg");
    if (autoshotBlob) {
      formData.append("autoshot", autoshotBlob, "autoshot.jpg");
    }
    if (videoDurationSeconds !== undefined) {
      formData.append("video_duration_seconds", String(videoDurationSeconds));
    }
    if (videoSizeBytes !== undefined) {
      formData.append("video_size_bytes", String(videoSizeBytes));
    }

    return this.http.uploadWithSession<LivenessSubmitResponse>(
      "/v1/sdk/liveness/submit/",
      formData
    );
  }

  /**
   * Poll the public SDK status endpoint for the final verification result.
   * Safe to call after the session has been revoked (no auth required).
   *
   * GET /v1/liveness/<entryId>/sdk-result/
   */
  async pollResult(entryId: number): Promise<{
    id: number;
    status: string;
    failure_reason: string | null;
    completed_at: string | null;
  }> {
    return (this.http as any).publicGet(`/v1/liveness/${entryId}/sdk-result/`);
  }

  /**
   * Full liveness check — orchestrates the entire flow:
   *
   * 1. Take autoshot selfie from camera
   * 2. Create liveness entry on backend (with challenge actions + selfie + optional doc)
   * 3. Run action detection loop (camera + MediaPipe)
   * 4. Record video and submit to backend
   * 5. Return submit response (status: "processing")
   *
   * @param container - DOM element to mount the liveness UI into
   * @param params - identifier info and optional document image
   * @param actions - override challenge actions (default: BLINK, TURN_LEFT, OPEN_MOUTH)
   */
  async startCheck(
    container: HTMLElement,
    params: {
      identifier: string;
      identifier_type: string;
      country: string;
      id_file?: File | Blob; // Legacy support
      document?: File | Blob; // Front document field (matches backend)
      document_front?: File | Blob; // Legacy support for backward compatibility
      document_back?: File | Blob; // Back document field (optional)
      identity_check?: number;
      verification_type?: "data_verification" | "document_verification" | "liveness_only";
    },
    actions?: ChallengeAction[],
    existingEntryId?: number
  ): Promise<LivenessSubmitResponse> {
    const camera = new CameraManager();
    const recorder = new VideoRecorder();
    const faceEngine = new FaceDetectorEngine();
    const ui = new LivenessUI();

    // Tracks whether cleanup has already been called (so the finally block
    // doesn't double-destroy when retry takes over).
    let cleanedUp = false;
    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      faceEngine.destroy();
      camera.stop();
      ui.unmount();
    };

    try {
      // 1. Open camera (with permission error handling)
      let stream: MediaStream;
      try {
        stream = await camera.open();
      } catch (camErr: any) {
        if (camErr.name === "NotAllowedError" || camErr.name === "PermissionDeniedError") {
          throw new Error("Camera access denied. Please allow camera permission in your browser settings and try again.");
        }
        if (camErr.name === "NotFoundError" || camErr.name === "DevicesNotFoundError") {
          throw new Error("No camera found. Please connect a camera and try again.");
        }
        throw new Error(`Camera error: ${camErr.message || "Unable to access camera"}`);
      }

      // 2. Document capture step — document_verification only, when no id_file pre-supplied
      let capturedDocBlob: Blob | undefined = params.id_file as Blob | undefined;
      if (!capturedDocBlob && params.verification_type === "document_verification") {
        capturedDocBlob = await ui.mountDocumentCapture(container, stream);
      }

      // 3. Create a temporary video element to capture autoshot
      const tempVideo = document.createElement("video");
      tempVideo.srcObject = stream;
      tempVideo.muted = true;
      tempVideo.playsInline = true;
      await tempVideo.play();

      // Wait for face to be detected before capturing autoshot
      ui.mount(container, { actions: [], time_limit_seconds: 0, instruction: "Centre your face in the oval" });
      const uiVideo = ui.getVideoElement();
      if (uiVideo) {
        uiVideo.srcObject = stream;
        await uiVideo.play();
      } else {
        tempVideo.style.cssText = "width:100%;border-radius:12px;transform:scaleX(-1);";
        container.appendChild(tempVideo);
      }

      await faceEngine.init();
      ui.updateInstruction("Centre your face in the oval");

      // Wait until face is stably centred (5 consecutive detected frames, max 12 s)
      const videoForDetection = uiVideo || tempVideo;
      await new Promise<void>((resolve) => {
        let attempts = 0;
        let stableFrames = 0;
        const STABLE_REQUIRED = 5;
        const maxAttempts = 120; // 12 seconds at 100ms intervals
        const checkFace = () => {
          attempts++;
          if (videoForDetection.readyState >= 2) {
            const detection = faceEngine.detect(videoForDetection, performance.now());
            if (detection.faceDetected) {
              stableFrames++;
              if (stableFrames >= STABLE_REQUIRED) {
                resolve();
                return;
              }
            } else {
              stableFrames = 0; // reset on any lost-face frame
              ui.updateInstruction("Move your face into the oval");
            }
          }
          if (attempts >= maxAttempts) {
            resolve(); // proceed anyway after timeout
            return;
          }
          setTimeout(checkFace, 100);
        };
        checkFace();
      });

      ui.updateInstruction("Hold still — capturing your selfie...");
      await new Promise((r) => setTimeout(r, 700)); // wait for camera auto-focus

      // 3. Capture autoshot (selfie) — face stable, camera focused
      const autoshotBlob = await this.captureFrame(videoForDetection);
      tempVideo.pause();

      // Validate autoshot blob is not empty
      if (!autoshotBlob || autoshotBlob.size === 0) {
        throw new Error("Failed to capture autoshot selfie. Please ensure camera is working.");
      }

      // 4. Create liveness entry on backend (skip if retrying with existing entry)
      const challengeActions = actions || ["BLINK", "TURN_LEFT", "OPEN_MOUTH"];
      let entry: LivenessCreateResponse;

      if (existingEntryId) {
        // Reuse existing entry on retry (D1: prevent duplicate wallet charges)
        entry = { id: existingEntryId, challenge_actions: challengeActions, status: "pending", client_id: "", expires_at: null };
      } else {
        const deviceMeta = collectDeviceMetadata();
        entry = await this.create({
          identifier: params.identifier,
          identifier_type: params.identifier_type,
          country: params.country,
          challenge_actions: challengeActions,
          autoshot_file: autoshotBlob,
          document: params.document || params.document_front || params.id_file || capturedDocBlob,
          document_back: params.document_back,
          identity_check: params.identity_check,
          device_type: deviceMeta.device_type,
          os_name: deviceMeta.os_name,
          browser_timezone: deviceMeta.browser_timezone,
          fingerprint_id: deviceMeta.fingerprint_id,
        });
      }

      // 5. Mount UI with challenge actions from backend
      const uiChallenge = {
        actions: entry.challenge_actions,
        time_limit_seconds: 45,
        instruction: "Complete each action below to verify it's you",
      };

      // Re-mount UI with challenge actions (face engine already initialized)
      ui.unmount();
      const videoEl = ui.mount(container, uiChallenge);
      videoEl.srcObject = stream;
      await videoEl.play();

      // 7. Start recording
      recorder.start(stream);
      ui.updateInstruction(uiChallenge.instruction);

      // 8. Run detection loop (sequential — one action at a time)
      const actionDetector = new ActionDetector(entry.challenge_actions);
      let lastAction: string | null = null;

      const detectionResult = await new Promise<"completed" | "timeout">(
        (resolve) => {
          let animFrameId: number;

          const timeout = setTimeout(() => {
            cancelAnimationFrame(animFrameId);
            resolve("timeout");
          }, uiChallenge.time_limit_seconds * 1000);

          const detectLoop = () => {
            if (videoEl.readyState >= 2) {
              const detection = faceEngine.detect(
                videoEl,
                performance.now()
              );

              const states = actionDetector.check(detection);
              ui.updateActions(states);

              const current = actionDetector.getCurrentAction();

              if (!detection.faceDetected) {
                ui.updateInstruction("Move your face into the oval");
              } else if (current) {
                if (current !== lastAction) {
                  lastAction = current;
                  ui.updateInstruction(this.describeAction(current));
                }
              } else {
                ui.updateInstruction("All actions completed! ✓");
              }

              if (actionDetector.allCompleted()) {
                clearTimeout(timeout);
                cancelAnimationFrame(animFrameId);
                resolve("completed");
                return;
              }
            }

            animFrameId = requestAnimationFrame(detectLoop);
          };

          detectLoop();
        }
      );

      // ── On timeout: show retry screen, clean up, restart ──────────
      if (detectionResult === "timeout") {
        // IMPORTANT: mark cleanedUp=true BEFORE returning the new Promise so
        // the outer `finally` block does NOT call cleanup() immediately —
        // which would destroy the UI before the user sees the timeout screen.
        cleanedUp = true;

        recorder.stop().catch(() => { }); // discard partial recording

        return new Promise<LivenessSubmitResponse>((resolveRetry, rejectRetry) => {
          ui.showTimeout(() => {
            // User clicked "Try Again" — now it is safe to destroy this attempt
            faceEngine.destroy();
            camera.stop();
            ui.unmount();

            // Restart liveness with same entry ID to prevent duplicate charges (D1)
            // Pass capturedDocBlob forward so document scan step is not repeated
            this.startCheck(container, { ...params, id_file: capturedDocBlob }, actions, entry.id)
              .then(resolveRetry)
              .catch(rejectRetry);
          });
          // showTimeout waits for the user to click — the promise stays pending.
        });
      }

      // ── On completion: stop recording, submit, show success ────────

      // Wait for user to return to neutral face after last action before
      // capturing snapshot — prevents mid-action pose from skewing DeepFace
      // embeddings (open mouth / head turned = high cosine distance).
      ui.updateInstruction("Almost done — hold still...");
      await new Promise((r) => setTimeout(r, 800));

      // Capture a snapshot frame while the video is still playing
      const snapshotBlob = await this.captureFrame(videoEl);

      // 9. Stop recording
      const { blob: videoBlob, durationSeconds } = await recorder.stop();

      // 10. Submit to backend — always include autoshot so document-flow
      // entries (which bypassed create() via existingEntryId) still get it.
      const submitResult = await this.submit(
        entry.id,
        videoBlob,
        snapshotBlob,
        autoshotBlob,
        durationSeconds,
        videoBlob.size
      );

      // 11. Show success overlay briefly before the flow advances
      await ui.showResult(
        submitResult.status === "failed" ? "failed" : "processing",
        submitResult.id
      );

      return submitResult;

    } finally {
      cleanup();
    }
  }

  /**
   * Capture a single sharp frame from a video element as a JPEG Blob.
   * Retries up to maxAttempts times (300 ms apart) if the Laplacian blur
   * score is below threshold, then falls back to the best frame captured.
   */
  private async captureFrame(
    video: HTMLVideoElement,
    maxAttempts = 4
  ): Promise<Blob> {
    const MIN_BLUR_SCORE = 80; // Laplacian variance — above this = acceptably sharp
    const RETRY_DELAY_MS = 300;
    let bestBlob: Blob | null = null;
    let bestScore = -1;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Failed to get canvas context");
      ctx.drawImage(video, 0, 0);

      const score = this._computeBlurScore(canvas);

      const blob = await new Promise<Blob | null>((res) =>
        canvas.toBlob((b) => res(b), "image/jpeg", 0.92)
      );

      if (blob && score > bestScore) {
        bestBlob = blob;
        bestScore = score;
      }

      if (score >= MIN_BLUR_SCORE) break; // sharp enough — stop early

      if (attempt < maxAttempts - 1) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
      }
    }

    if (bestBlob) return bestBlob;
    throw new Error("Failed to capture frame");
  }

  /**
   * Laplacian variance blur metric on a centre-cropped region (320×240 max).
   * Higher score = sharper image.  Score < 80 is considered blurry.
   */
  private _computeBlurScore(canvas: HTMLCanvasElement): number {
    const ctx = canvas.getContext("2d");
    if (!ctx) return 0;

    // Sample a 320×240 centre crop for performance
    const sw = Math.min(canvas.width, 320);
    const sh = Math.min(canvas.height, 240);
    const sx = Math.floor((canvas.width - sw) / 2);
    const sy = Math.floor((canvas.height - sh) / 2);

    const { data, width, height } = ctx.getImageData(sx, sy, sw, sh);

    // Convert to greyscale
    const gray = new Float32Array(width * height);
    for (let i = 0; i < width * height; i++) {
      gray[i] =
        0.299 * data[i * 4] +
        0.587 * data[i * 4 + 1] +
        0.114 * data[i * 4 + 2];
    }

    // Laplacian convolution [0,1,0 / 1,-4,1 / 0,1,0] — sum of squared responses
    let sum = 0;
    let n = 0;
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const l =
          gray[(y - 1) * width + x] +
          gray[(y + 1) * width + x] +
          gray[y * width + (x - 1)] +
          gray[y * width + (x + 1)] -
          4 * gray[y * width + x];
        sum += l * l;
        n++;
      }
    }
    return n > 0 ? sum / n : 0;
  }

  private describeAction(action: string): string {
    const descriptions: Record<string, string> = {
      BLINK: "Blink your eyes",
      TURN_LEFT: "Turn your head left",
      TURN_RIGHT: "Turn your head right",
      TURN_HEAD: "Turn your head side to side",
      OPEN_MOUTH: "Open your mouth wide",
    };
    return descriptions[action] || action.replace(/_/g, " ").toLowerCase();
  }
}
